/**
 * What a run's log says happened, without reading sixty thousand lines of it.
 *
 * IM-35 (what a plan would change) and IM-165 (how long an apply or destroy
 * took, and where the time went) are **one parser**, because they read the same
 * lines: the plan an apply prints *is* its plan, and the progress lines under it
 * are its timeline. Two parsers would be two opinions about which line is a
 * resource header.
 *
 * ## Why the log
 *
 * For the reason `deployments/outputs.ts` gives: there is nowhere else. The plan
 * file dies with the container and the state is in the customer's bucket, which
 * the diagnostics role is denied `s3:GetObject` on. What leaves their account is
 * the log, already redacted there.
 *
 * ## What a real one looks like
 *
 * Measured on a real fresh apply: 62,388 lines, of which the plan body is
 * ~59,000 — and two thirds of *that* is ten `kubectl_manifest` CRD schemas, one
 * of them 19,091 lines on its own. The part a person wants is 461 header lines
 * and a few hundred progress lines. About a fifth of all lines carry terminal
 * escapes, including in front of the headers, so everything here reads through
 * `stripAnsi` first.
 *
 * ## Three layers, and the first two are for IM-166 too
 *
 * - `classifyLine` says what one line is, from its shape alone.
 * - `lineClassifier` is the same with memory: it knows a line is inside a
 *   resource's attribute body, which no single line can say (a heredoc in a CRD
 *   schema looks like anything). This is what folding a body needs.
 * - `runLogAccumulator` / `parseRunLog` turn the classified lines into a
 *   `RunLogSummary`.
 *
 * All linear: one pass, one `stripAnsi` per line, and every pattern guarded by a
 * cheap prefix check so the ~59,000 body lines never reach a regex that could
 * backtrack.
 *
 * ## What it refuses to do
 *
 * - **It never invents a duration.** Terraform's own lines carry no clock, so a
 *   phase is timed only by the installer's timestamped lines around it, and a
 *   phase with no timestamp at an edge reports no duration rather than a guess.
 * - **It never picks between two counts.** The headers and Terraform's `Plan:`
 *   line are both counted, and when they disagree the summary says so.
 * - **It carries no log content.** Resource addresses, counts, durations and
 *   phase names from a fixed list — never an attribute value, an `[id=…]`, an
 *   installer message or an error's text. That is what lets it be read at
 *   `deployment:read`; see the route's note and `CLAUDE.md`.
 */
import { stripAnsi } from './ansi.js';
import { distinctFailures } from './failure-extract.js';
// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------
/** What Terraform says it will do to a resource. */
export const PLAN_ACTIONS = ['create', 'update', 'replace', 'destroy', 'read'];
/**
 * The installer's phases, as fixed labels.
 *
 * The installer marks its phases with timestamped `INFO` lines, and those lines
 * carry hostnames, cluster names and account ids — so what is *kept* is the
 * phase's id and a label written here, never the line. A marker this list does
 * not name starts nothing, which is the safe failure: a phase is folded into
 * the one before it rather than named from arbitrary text.
 *
 * Read off the installer's output for `9.5-r-20260831-0793` and later, from an
 * apply, a plan, a destroy and an `output` run. `terraform` has no marker: it is
 * detected from Terraform's own lines, which are the only reliable sign it has
 * started.
 */
export const RUN_PHASES = [
    { id: 'initialise', label: 'Initialising', marker: /^Starting initialization process/ },
    {
        id: 'checks',
        label: 'Pre-flight checks',
        marker: /^(?:Starting Terraform apply|Executing 'qrvey [a-z-]+' command)/,
    },
    { id: 'cleanup', label: 'Pre-destroy cleanup', marker: /^Starting Destroy process/ },
    { id: 'terraform', label: 'Terraform', marker: undefined },
    {
        id: 'readiness',
        label: 'Waiting for services to be ready',
        marker: /^Waiting up to .{1,40} for all deployments/,
    },
    { id: 'routing', label: 'Routing validation', marker: /^Gateway API Routing Validation/ },
    { id: 'post-deploy', label: 'Post-deploy steps', marker: /^Running post-deployment step/ },
    {
        id: 'bucket-teardown',
        label: 'Emptying and deleting buckets',
        marker: /All infrastructure destroyed EXCEPT the S3 buckets/,
    },
    {
        id: 'verification',
        label: 'Post-destroy verification',
        marker: /^=== Post-destroy verification ===/i,
    },
    {
        id: 'outputs',
        label: 'Reading outputs',
        marker: /^(?:Retrieving and printing deployment details|Retrieving Terraform outputs)/,
    },
];
const PHASE_LABEL = new Map(RUN_PHASES.map((p) => [p.id, p.label]));
const OTHER = { kind: 'other' };
const BODY = { kind: 'attribute-body' };
const WARNING = { kind: 'warning' };
const ERROR = { kind: 'error' };
const PLAN_START = { kind: 'plan-start' };
/** Longer than any real address by a wide margin; beyond it, a line is not one. */
export const MAX_ADDRESS_LENGTH = 300;
/*
 * A Terraform resource address, strictly.
 *
 * Strict because an address is the one piece of a line this parser keeps, so
 * it must not be a way for arbitrary text to survive into a stored summary: a
 * line that merely *contains* ` will be created` and is not an address is not a
 * header. Index keys are a number or a quoted string; for_each keys are
 * customer-chosen and carried as they are — see the note on the route.
 */
const INDEX = String.raw `(?:\[(?:\d+|"(?:[^"\\]|\\.)*")\])?`;
const NAME = String.raw `[A-Za-z_][\w-]*`;
const ADDRESS = new RegExp(String.raw `^(?:module\.${NAME}${INDEX}\.)*(?:data\.)?${NAME}\.${NAME}${INDEX}$`);
const isAddress = (text) => text.length <= MAX_ADDRESS_LENGTH && ADDRESS.test(text);
/*
 * Every header phrasing Terraform 1.x prints, and what it means. A deposed
 * object's header carries ` (deposed object 1a2b3c4d)` after the address, which
 * is stripped: it is the same resource.
 */
const HEADER = /^# (.+?)(?: \(deposed object \w+\))? (will be|must be|is tainted, so must be|has moved to) ?(.*)$/;
const headerAction = (verb, rest) => {
    if (verb === 'has moved to')
        return 'other';
    if (verb === 'must be' || verb === 'is tainted, so must be') {
        return rest.startsWith('replaced') ? 'replace' : undefined;
    }
    if (rest === 'created')
        return 'create';
    if (rest === 'destroyed')
        return 'destroy';
    if (rest === 'updated in-place')
        return 'update';
    if (rest === 'read during apply')
        return 'read';
    if (rest.startsWith('replaced'))
        return 'replace';
    // `will be imported`, `will no longer be managed by Terraform`, `forgotten`.
    if (rest.startsWith('imported') || rest.startsWith('forgotten'))
        return 'other';
    if (rest.startsWith('no longer be managed'))
        return 'other';
    return undefined;
};
const PROGRESS = /^(.+?): (Creating|Destroying|Modifying|Reading|Still creating|Still destroying|Still modifying|Still reading|Creation complete|Destruction complete|Modifications complete|Read complete)(?:\.\.\.)?(.*)$/;
const VERB_ACTION = {
    Creating: 'create',
    'Still creating': 'create',
    'Creation complete': 'create',
    Destroying: 'destroy',
    'Still destroying': 'destroy',
    'Destruction complete': 'destroy',
    Modifying: 'update',
    'Still modifying': 'update',
    'Modifications complete': 'update',
    Reading: 'read',
    'Still reading': 'read',
    'Read complete': 'read',
};
const DURATION = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+(?:\.\d+)?)s)?$/;
/** Terraform's `1h2m3s`, `9m43s`, `10s`, as seconds. `undefined` if not one. */
export const parseTerraformDuration = (text) => {
    if (text === '')
        return undefined;
    const m = DURATION.exec(text);
    if (m === null)
        return undefined;
    const [, h, min, s] = m;
    if (h === undefined && min === undefined && s === undefined)
        return undefined;
    return Number(h ?? 0) * 3600 + Number(min ?? 0) * 60 + Number(s ?? 0);
};
// `[10s elapsed]` for a create, `[id=…, 10s elapsed]` for a destroy or update.
const ELAPSED = /(?:\[|, )(\S+) elapsed\]/;
const AFTER = /^ after (\S+)/;
const PLAN_LINE = /^Plan: (?:(\d+) to import, )?(\d+) to add, (\d+) to change, (\d+) to destroy/;
const OUTCOME = /^(Apply|Destroy) complete! Resources: (.*)$/;
const OUTCOME_PART = /(\d+) (imported|added|changed|destroyed)/g;
const STAMP = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z) (DEBUG|INFO|WARN|WARNING|ERROR) ?(.*)$/;
/*
 * Frame heads. `failure-extract.ts` owns reading an error block; this only
 * says a line starts one, so the viewer can find it and the summary can count.
 */
const FRAMED = /^│\s?(Error|Warning): /;
/** Indented lines whose shape alone says they belong to a plan's resource body. */
const BODY_SHAPE = /^\s+(?:[+~-] |-\/\+ |\+\/- |<= |[}\]]|# \(|(?:resource|data) ")/;
const phaseOf = (message) => {
    for (const phase of RUN_PHASES) {
        if (phase.marker !== undefined && phase.marker.test(message))
            return phase.id;
    }
    return undefined;
};
const classifyClean = (clean) => {
    if (clean === '')
        return OTHER;
    const first = clean.charCodeAt(0);
    const trimmed = first === 32 ? clean.trimStart() : clean;
    // Headers are indented two spaces; anything starting `# ` after that is a
    // candidate, and `isAddress` is what makes it one.
    if (trimmed.startsWith('# ')) {
        const m = HEADER.exec(trimmed);
        if (m !== null) {
            const address = m[1] ?? '';
            const action = headerAction(m[2] ?? '', m[3] ?? '');
            if (action !== undefined && isAddress(address)) {
                return { kind: 'resource-header', address, action };
            }
        }
        return first === 32 ? BODY : OTHER;
    }
    // Column-0 lines: Terraform's progress and summaries, and the installer.
    if (first !== 32) {
        const c = clean.charCodeAt(0);
        // `2026-…` — the installer.
        if (c >= 48 && c <= 57) {
            const m = STAMP.exec(clean);
            if (m !== null) {
                const at = m[1] ?? '';
                const phase = phaseOf(m[3] ?? '');
                return phase === undefined
                    ? { kind: 'installer', at, level: m[2] ?? '' }
                    : { kind: 'installer-phase', at, phase };
            }
        }
        if (clean.startsWith('Plan: ')) {
            const m = PLAN_LINE.exec(clean);
            if (m !== null) {
                return {
                    kind: 'plan-summary',
                    counts: {
                        add: Number(m[2]),
                        change: Number(m[3]),
                        destroy: Number(m[4]),
                        ...(m[1] === undefined ? {} : { import: Number(m[1]) }),
                    },
                };
            }
        }
        if (clean.startsWith('No changes.')) {
            return { kind: 'plan-summary', counts: { add: 0, change: 0, destroy: 0 } };
        }
        if (clean.startsWith('Apply complete!') || clean.startsWith('Destroy complete!')) {
            const m = OUTCOME.exec(clean);
            if (m !== null) {
                const counts = {};
                for (const part of (m[2] ?? '').matchAll(OUTCOME_PART)) {
                    counts[part[2] ?? ''] = Number(part[1]);
                }
                return {
                    kind: 'apply-summary',
                    outcome: {
                        command: m[1] === 'Destroy' ? 'destroy' : 'apply',
                        added: counts['added'] ?? 0,
                        changed: counts['changed'] ?? 0,
                        destroyed: counts['destroyed'] ?? 0,
                        ...(counts['imported'] === undefined ? {} : { imported: counts['imported'] }),
                    },
                };
            }
        }
        if (clean.startsWith('Terraform will perform the following actions') ||
            clean.startsWith('Terraform used the selected providers')) {
            return PLAN_START;
        }
        if (clean.startsWith('│')) {
            const m = FRAMED.exec(clean);
            if (m !== null)
                return m[1] === 'Error' ? ERROR : WARNING;
            return OTHER;
        }
        if (clean.startsWith('Error: '))
            return ERROR;
        if (clean.startsWith('Warning: '))
            return WARNING;
        // Progress: cheap guard first, since a 19,000-line CRD body is all
        // indented and never gets here, but the Outputs block does.
        if (clean.includes(': ') && (clean.includes('...') || clean.includes(' complete after'))) {
            const m = PROGRESS.exec(clean);
            if (m !== null && isAddress(m[1] ?? '')) {
                const address = m[1] ?? '';
                const verb = m[2] ?? '';
                const rest = m[3] ?? '';
                const action = VERB_ACTION[verb];
                if (action !== undefined) {
                    if (verb.startsWith('Still ')) {
                        const e = ELAPSED.exec(rest);
                        return {
                            kind: 'progress-tick',
                            address,
                            action,
                            elapsedSeconds: e === null ? undefined : parseTerraformDuration(e[1] ?? ''),
                        };
                    }
                    if (verb.endsWith(' complete')) {
                        const a = AFTER.exec(rest);
                        return {
                            kind: 'progress-complete',
                            address,
                            action,
                            seconds: a === null ? undefined : parseTerraformDuration(a[1] ?? ''),
                        };
                    }
                    return { kind: 'progress-start', address, action };
                }
            }
        }
        return OTHER;
    }
    // Indented and not a header: a body line if it is shaped like one.
    if (trimmed.startsWith('│')) {
        const m = FRAMED.exec(trimmed);
        if (m !== null)
            return m[1] === 'Error' ? ERROR : WARNING;
    }
    return BODY_SHAPE.test(clean) ? BODY : OTHER;
};
/**
 * What one line is, from its shape alone.
 *
 * Context-free, so it can be asked about any line in any order — and therefore
 * cannot see that a line of a heredoc inside a CRD schema is part of a resource
 * body. Use `lineClassifier` when lines arrive in order, which is always the
 * case for a log.
 */
export const classifyLine = (line) => classifyClean(stripAnsi(line));
/*
 * One object per combination, shared, so the ~60,000 lines of a real log do
 * not each allocate one — and none of them is inside a frame anyway.
 */
const FRAMES = Object.fromEntries(['open', 'body', 'close'].flatMap((part) => ['error', 'warning', undefined].map((severity) => [
    `${part}:${String(severity)}`,
    { part, severity },
])));
const frameOf = (part, severity) => FRAMES[`${part}:${String(severity)}`] ?? { part, severity };
/**
 * A classifier with memory, for lines fed in order.
 *
 * Three things only a sequence can tell:
 *
 * - **A resource's body runs from its header to the next column-0 line.** Its
 *   contents can look like anything — a CRD's `description = <<-EOT` heredoc
 *   is free text — so shape alone misses most of it. Inside a body every line
 *   that is not a header is `attribute-body`; outside one, an indented line
 *   shaped like a body line (the `Changes to Outputs:` block) is `other`.
 * - **The compact `Warnings:` list is one warning per `- ` item**, which only
 *   means something under that heading.
 * - **A framed diagnostic runs from `╷` to `╵`** (IM-166), and its severity is
 *   on the line after the one that opens it. See `DiagnosticFrame`. The rule
 *   is `failuresIn`'s: a line not shaped like a frame line ends the frame, so
 *   an interleaved or truncated one does not swallow the log after it.
 *
 * Returns the stripped line with the classification, so a caller that renders
 * lines does not strip them twice.
 */
export const lineClassifier = () => {
    let inBody = false;
    let inCompactWarnings = false;
    /** `false` outside a frame; inside one, its severity once the head says. */
    let frame = false;
    const unframed = (line) => {
        const text = stripAnsi(line);
        const found = classifyClean(text);
        const columnZero = text !== '' && text.charCodeAt(0) !== 32;
        if (found.kind === 'resource-header') {
            inBody = true;
            inCompactWarnings = false;
            return { ...found, text };
        }
        if (columnZero) {
            inBody = false;
            if (text.startsWith('Warnings:')) {
                inCompactWarnings = true;
                return { kind: 'other', text };
            }
            if (inCompactWarnings && text.startsWith('- '))
                return { kind: 'warning', text };
            if (inCompactWarnings && !text.startsWith('  '))
                inCompactWarnings = false;
            return { ...found, text };
        }
        if (inBody) {
            // A framed diagnostic is never indented, so nothing indented here is one.
            return { kind: 'attribute-body', text };
        }
        return { ...(found.kind === 'attribute-body' ? OTHER : found), text };
    };
    return (line) => {
        const found = unframed(line);
        // Cheap guard first: a frame character is the first thing on its line,
        // indented or not, and the ~59,000 body lines never are one.
        const first = found.text.charCodeAt(0);
        const lead = first === 32 ? found.text.trimStart().charCodeAt(0) : first;
        const framing = lead === 0x2577 || lead === 0x2575 || lead === 0x2502;
        const trimmed = framing ? found.text.trim() : '';
        if (trimmed === '╷') {
            frame = undefined;
            return { ...found, frame: frameOf('open', undefined) };
        }
        if (frame === false)
            return found;
        if (trimmed === '╵') {
            const closing = frameOf('close', frame);
            frame = false;
            return { ...found, frame: closing };
        }
        if (!framing) {
            frame = false;
            return found;
        }
        if (frame === undefined && (found.kind === 'error' || found.kind === 'warning')) {
            frame = found.kind;
        }
        return { ...found, frame: frameOf('body', frame) };
    };
};
/** Every line, classified in order. The shape IM-166's viewer folds and filters. */
export const classifyLines = (lines) => {
    const next = lineClassifier();
    return lines.map(next);
};
// ---------------------------------------------------------------------------
// Addresses
// ---------------------------------------------------------------------------
/**
 * The resource type an address names: `aws_eks_cluster` for
 * `module.eks.aws_eks_cluster.this[0]`, `aws_iam_policy_document` for a data
 * source. Module segments are skipped, whatever their index.
 */
export const resourceTypeOf = (address) => {
    let rest = address;
    while (rest.startsWith('module.')) {
        const dot = nextSegment(rest, 'module.'.length);
        if (dot === -1)
            return rest;
        rest = rest.slice(dot + 1);
    }
    if (rest.startsWith('data.'))
        rest = rest.slice('data.'.length);
    const dot = rest.indexOf('.');
    return dot === -1 ? rest : rest.slice(0, dot);
};
/** The end of one `name[index]` segment, honouring quotes inside the index. */
const nextSegment = (text, from) => {
    let quoted = false;
    for (let i = from; i < text.length; i += 1) {
        const c = text[i];
        if (quoted) {
            if (c === '\\')
                i += 1;
            else if (c === '"')
                quoted = false;
        }
        else if (c === '"')
            quoted = true;
        else if (c === '.')
            return i;
    }
    return -1;
};
/**
 * Where an address belongs on the summary card.
 *
 * The top-level module when there is one — `module.eks`, `module.aurora` — since
 * that is how the installer is organised and how an engineer thinks about it.
 * A root resource groups by its type, which is what makes the ten CRD
 * `kubectl_manifest`s one line rather than ten; a root data source by
 * `data.<type>`. The module's own index is dropped, so a `for_each` module is
 * one component rather than one per key.
 */
export const componentOf = (address) => {
    if (address.startsWith('module.')) {
        const end = nextSegment(address, 'module.'.length);
        const segment = end === -1 ? address : address.slice(0, end);
        const bracket = segment.indexOf('[');
        return bracket === -1 ? segment : segment.slice(0, bracket);
    }
    if (address.startsWith('data.'))
        return `data.${resourceTypeOf(address)}`;
    return resourceTypeOf(address);
};
/**
 * The resources whose destroy or replace deserves to be read twice.
 *
 * **Conservative on purpose.** Each holds something a re-apply does not bring
 * back — data, or the cluster the whole product runs in. A longer list would
 * flag security groups and IAM attachments, which Terraform replaces all the
 * time, and a flag on every plan is a flag nobody reads; the destructive list is
 * already shown first and in full, so what this adds is the distinction between
 * "this replaces a policy" and "this replaces the database". Only destroys and
 * replaces are flagged: an update in place keeps the object.
 */
export const SENSITIVE_RESOURCE_TYPES = {
    aws_eks_cluster: 'The cluster every Qrvey service runs in. Replacing it is an outage.',
    aws_rds_cluster: 'The Aurora cluster holding the product database.',
    aws_rds_cluster_instance: 'An Aurora database instance.',
    aws_db_instance: 'An RDS database instance.',
    aws_opensearch_domain: 'The OpenSearch domain holding indexed datasets.',
    aws_elasticsearch_domain: 'The Elasticsearch domain holding indexed datasets.',
    aws_s3_bucket: 'A bucket. Its objects go with it.',
    aws_efs_file_system: 'A shared file system and everything on it.',
    aws_dynamodb_table: 'A DynamoDB table and its items.',
    aws_kms_key: 'An encryption key. Once its deletion window ends, whatever it encrypted cannot be read.',
};
/** Why this change is flagged, or `undefined` when it is not. */
export const sensitivityOf = (address, action) => {
    if (action !== 'destroy' && action !== 'replace')
        return undefined;
    if (address.startsWith('data.') || address.includes('.data.'))
        return undefined;
    return SENSITIVE_RESOURCE_TYPES[resourceTypeOf(address)];
};
// ---------------------------------------------------------------------------
// The summary
// ---------------------------------------------------------------------------
/** Bumped when the stored shape changes, so an old summary can be told apart. */
export const RUN_LOG_SUMMARY_VERSION = 1;
/*
 * Every list is capped, because a summary is stored (IM-35) and a plan's size
 * is the customer's. The caps are generous for any real Qrvey plan — 443
 * resources over ~40 components — and each cut is stated as "and N more".
 */
export const SUMMARY_LIMITS = {
    /** Destroys and replaces listed by address. */
    destructive: 100,
    /** Components listed. */
    components: 60,
    /** Addresses listed under one component. */
    addressesPerComponent: 40,
    /** Addresses listed across all components together. */
    addresses: 400,
    slowest: 10,
    inProgress: 20,
    phases: 40,
    failures: 10,
};
/** The whole summary is held under this, whatever the plan. See `boundSummary`. */
export const SUMMARY_BYTE_BUDGET = 96 * 1024;
const zeroCounts = () => ({
    create: 0,
    update: 0,
    replace: 0,
    destroy: 0,
    read: 0,
});
const zeroProgress = () => ({
    create: 0,
    update: 0,
    destroy: 0,
    read: 0,
});
export const runLogAccumulator = () => {
    const classify = lineClassifier();
    let lines = 0;
    // The current plan pass. Reset at each plan-start.
    let passes = 0;
    let headers = [];
    let planLine;
    let sawShape = false;
    let planOpen = false;
    let outcome;
    let applyStarted = false;
    let errors = 0;
    const warnings = { terraform: 0, installer: 0 };
    const running = new Map();
    const finishedSeconds = new Map();
    const completed = zeroProgress();
    const phases = [];
    const attempts = new Map();
    let current;
    let firstStamp;
    let lastStamp;
    const open = (id, startedAt) => {
        // The installer prints some markers once per step — four post-deploy steps
        // are one phase, not four attempts at it.
        if (current?.id === id && current.endedAt === undefined)
            return;
        if (current !== undefined && current.endedAt === undefined)
            current.endedAt = startedAt;
        const attempt = (attempts.get(id) ?? 0) + 1;
        attempts.set(id, attempt);
        current = { id, attempt, startedAt, endedAt: undefined };
        phases.push(current);
    };
    const terraformLine = () => {
        sawShape = true;
        if (current?.id !== 'terraform' || current.endedAt !== undefined)
            open('terraform', lastStamp);
    };
    const stamp = (at) => {
        if (firstStamp === undefined)
            firstStamp = at;
        lastStamp = at;
        // Terraform prints no clock, so its phase ends at the first installer line
        // after it — the earliest moment the installer was back in control.
        if (current?.id === 'terraform' && current.endedAt === undefined)
            current.endedAt = at;
    };
    const push = (batch) => {
        for (const raw of batch) {
            lines += 1;
            const line = classify(raw);
            switch (line.kind) {
                case 'plan-start':
                    terraformLine();
                    // `Terraform used the selected providers` and `will perform` arrive
                    // together; only the first of a pair begins a pass.
                    if (!planOpen) {
                        passes += 1;
                        headers = [];
                        planLine = undefined;
                        planOpen = true;
                    }
                    break;
                case 'resource-header':
                    terraformLine();
                    if (!planOpen) {
                        // A header with no intro — a log that starts mid-plan.
                        passes += 1;
                        headers = [];
                        planLine = undefined;
                        planOpen = true;
                    }
                    if (line.action !== 'other')
                        headers.push({ address: line.address, action: line.action });
                    break;
                case 'plan-summary':
                    terraformLine();
                    planLine = line.counts;
                    planOpen = false;
                    break;
                case 'progress-start':
                    terraformLine();
                    if (line.action !== 'read')
                        applyStarted = true;
                    running.set(line.address, { address: line.address, action: line.action });
                    break;
                case 'progress-tick':
                    terraformLine();
                    running.set(line.address, {
                        address: line.address,
                        action: line.action,
                        ...(line.elapsedSeconds === undefined ? {} : { elapsedSeconds: line.elapsedSeconds }),
                    });
                    break;
                case 'progress-complete': {
                    terraformLine();
                    if (line.action !== 'read')
                        applyStarted = true;
                    running.delete(line.address);
                    completed[line.action] += 1;
                    if (line.seconds !== undefined) {
                        const seen = finishedSeconds.get(line.address);
                        if (seen === undefined || seen.seconds < line.seconds) {
                            finishedSeconds.set(line.address, {
                                address: line.address,
                                action: line.action,
                                seconds: line.seconds,
                            });
                        }
                    }
                    break;
                }
                case 'apply-summary':
                    terraformLine();
                    outcome = line.outcome;
                    planOpen = false;
                    break;
                case 'installer-phase':
                    stamp(line.at);
                    open(line.phase, line.at);
                    break;
                case 'installer':
                    stamp(line.at);
                    if (line.level === 'WARN' || line.level === 'WARNING')
                        warnings.installer += 1;
                    break;
                case 'warning':
                    warnings.terraform += 1;
                    break;
                case 'error':
                    errors += 1;
                    break;
                case 'attribute-body':
                case 'other':
                    break;
            }
        }
    };
    const summary = (options = {}) => {
        const onlyDestroying = completed.create === 0 &&
            completed.update === 0 &&
            [...running.values()].every((r) => r.action === 'destroy' || r.action === 'read') &&
            (planLine?.add ?? 0) === 0;
        const shape = outcome !== undefined
            ? outcome.command
            : applyStarted
                ? onlyDestroying
                    ? 'destroy'
                    : 'apply'
                : sawShape
                    ? 'plan'
                    : 'none';
        const plan = passes > 0 || planLine !== undefined ? planSummary(headers, planLine, passes) : undefined;
        const complete = options.complete !== false;
        let parsed = 'complete';
        let reason;
        if (shape === 'none') {
            parsed = 'unparseable';
            reason =
                lines === 0
                    ? 'The log is empty.'
                    : 'Nothing in this log looks like a Terraform plan, apply or destroy.';
        }
        else if (!complete) {
            parsed = 'partial';
            reason = options.incompleteReason ?? 'Only part of the log has been read.';
        }
        else if (planOpen) {
            parsed = 'partial';
            reason = 'The log stops partway through the plan.';
        }
        else if (applyStarted && outcome === undefined && errors === 0) {
            parsed = 'partial';
            reason = 'Changes started, but the log ends before Terraform said how they finished.';
        }
        // Whether the log reached its end: a partial one's last phase is still going.
        const finished = parsed !== 'partial';
        const inFlight = [...running.values()];
        const timeline = {
            phases: phases.slice(0, SUMMARY_LIMITS.phases).map((p, index, all) => {
                const last = index === all.length - 1 && index === phases.length - 1;
                // The last phase of a finished log ends at its last timestamp; of an
                // unfinished one, it has not ended.
                const endedAt = p.endedAt ?? (last && finished ? lastStamp : undefined);
                const isOpen = p.endedAt === undefined && !(last && finished);
                const seconds = secondsBetween(p.startedAt, endedAt);
                return {
                    id: p.id,
                    label: PHASE_LABEL.get(p.id) ?? p.id,
                    attempt: p.attempt,
                    ...(p.startedAt === undefined ? {} : { startedAt: p.startedAt }),
                    ...(endedAt === undefined ? {} : { endedAt }),
                    ...(seconds === undefined || isOpen ? {} : { seconds }),
                    open: isOpen,
                };
            }),
            ...(firstStamp !== undefined && lastStamp !== undefined && firstStamp !== lastStamp
                ? (() => {
                    const total = secondsBetween(firstStamp, lastStamp);
                    return total === undefined ? {} : { totalSeconds: total };
                })()
                : {}),
            slowest: [...finishedSeconds.values()]
                .sort((a, b) => b.seconds - a.seconds || a.address.localeCompare(b.address))
                .slice(0, SUMMARY_LIMITS.slowest),
            inProgress: inFlight.slice(0, SUMMARY_LIMITS.inProgress),
            moreInProgress: Math.max(0, inFlight.length - SUMMARY_LIMITS.inProgress),
            completed: { ...completed },
        };
        return {
            version: RUN_LOG_SUMMARY_VERSION,
            parsed,
            ...(reason === undefined ? {} : { reason }),
            shape,
            lines,
            ...(plan === undefined ? {} : { plan }),
            ...(outcome === undefined ? {} : { outcome }),
            timeline,
            warnings: { ...warnings },
        };
    };
    return { push, summary };
};
const secondsBetween = (from, to) => {
    if (from === undefined || to === undefined)
        return undefined;
    const a = Date.parse(from);
    const b = Date.parse(to);
    if (Number.isNaN(a) || Number.isNaN(b) || b < a)
        return undefined;
    return Math.round((b - a) / 1000);
};
const planSummary = (headers, planLine, passes) => {
    const counts = zeroCounts();
    for (const h of headers)
        counts[h.action] += 1;
    let agreement;
    let disagreement;
    if (planLine === undefined)
        agreement = 'no-plan-line';
    else if (headers.length === 0 && planLine.add + planLine.change + planLine.destroy > 0) {
        agreement = 'no-headers';
    }
    else {
        // A replace is one add and one destroy on Terraform's line.
        const add = counts.create + counts.replace;
        const destroy = counts.destroy + counts.replace;
        const parts = [];
        if (add !== planLine.add)
            parts.push(`${String(add)} to add against ${String(planLine.add)}`);
        if (counts.update !== planLine.change) {
            parts.push(`${String(counts.update)} to change against ${String(planLine.change)}`);
        }
        if (destroy !== planLine.destroy) {
            parts.push(`${String(destroy)} to destroy against ${String(planLine.destroy)}`);
        }
        agreement = parts.length === 0 ? 'agrees' : 'disagrees';
        if (parts.length > 0) {
            disagreement = `The resource list and Terraform's own Plan line disagree: ${parts.join(', ')}. Both are shown; neither is picked.`;
        }
    }
    const destructiveAll = [];
    const rest = [];
    for (const h of headers) {
        if (h.action === 'destroy' || h.action === 'replace') {
            const sensitive = sensitivityOf(h.address, h.action);
            destructiveAll.push({
                address: h.address,
                action: h.action,
                component: componentOf(h.address),
                ...(sensitive === undefined ? {} : { sensitive }),
            });
        }
        else
            rest.push(h);
    }
    // Sensitive first, then replaces before destroys, then in the order printed.
    const rank = (c) => (c.sensitive === undefined ? 2 : 0) + (c.action === 'replace' ? 0 : 1);
    const destructive = destructiveAll
        .map((c, i) => ({ c, i }))
        .sort((a, b) => rank(a.c) - rank(b.c) || a.i - b.i)
        .map(({ c }) => c);
    const byComponent = new Map();
    for (const h of rest) {
        const key = componentOf(h.address);
        const list = byComponent.get(key);
        if (list === undefined)
            byComponent.set(key, [h]);
        else
            list.push(h);
    }
    const ordered = [...byComponent.entries()].sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]));
    let budget = SUMMARY_LIMITS.addresses;
    const components = ordered
        .slice(0, SUMMARY_LIMITS.components)
        .map(([component, list]) => {
        const c = zeroCounts();
        for (const h of list)
            c[h.action] += 1;
        const take = Math.max(0, Math.min(list.length, SUMMARY_LIMITS.addressesPerComponent, budget));
        budget -= take;
        return {
            component,
            total: list.length,
            counts: c,
            addresses: list.slice(0, take).map((h) => ({ address: h.address, action: h.action })),
            more: list.length - take,
        };
    });
    return {
        counts,
        ...(planLine === undefined ? {} : { planLine }),
        agreement,
        ...(disagreement === undefined ? {} : { disagreement }),
        destructive: destructive.slice(0, SUMMARY_LIMITS.destructive),
        moreDestructive: Math.max(0, destructive.length - SUMMARY_LIMITS.destructive),
        sensitiveCount: destructive.filter((c) => c.sensitive !== undefined).length,
        components,
        moreComponents: Math.max(0, ordered.length - SUMMARY_LIMITS.components),
        passes,
    };
};
/**
 * The summary of a whole log.
 *
 * The accumulator, plus the errors — read by `distinctFailures`, which owns
 * Terraform's diagnostic frame, and kept here as the resource and location
 * each names and how many attempts hit it. **Not the message.** The message is
 * log content, and it is on the log panel for whoever may read logs.
 */
export const parseRunLog = (lines, options = {}) => {
    const acc = runLogAccumulator();
    acc.push(lines);
    const base = acc.summary(options);
    const found = distinctFailures(lines);
    return boundSummary({
        ...base,
        failures: {
            count: found.length,
            distinct: found.slice(0, SUMMARY_LIMITS.failures).map((f) => ({
                ...(f.resource === undefined ? {} : { resource: f.resource }),
                ...(f.at === undefined ? {} : { at: f.at }),
                attempts: f.attempts,
            })),
        },
    });
};
/**
 * Holds a summary under `SUMMARY_BYTE_BUDGET`, whatever the plan.
 *
 * The per-list caps already keep any real plan far under it; this is the
 * guarantee for the one that is not real — thousands of resources with
 * three-hundred-character addresses. It drops component addresses first, from
 * the largest component down, then the destructive list's tail, and counts
 * every cut into the `more` it belongs to. A summary is stored beside the run,
 * and the item limit is DynamoDB's, not ours to discover in production.
 */
export const boundSummary = (summary) => {
    const size = (s) => utf8Length(JSON.stringify(s));
    if (summary.plan === undefined || size(summary) <= SUMMARY_BYTE_BUDGET)
        return summary;
    let plan = summary.plan;
    let bounded = summary;
    const components = plan.components.map((c) => ({ ...c, addresses: [...c.addresses] }));
    const order = components
        .map((c, i) => ({ i, n: c.addresses.length }))
        .sort((a, b) => b.n - a.n)
        .map((x) => x.i);
    for (const i of order) {
        const c = components[i];
        if (c === undefined)
            continue;
        c.more += c.addresses.length;
        c.addresses = [];
        plan = { ...plan, components };
        bounded = { ...summary, plan };
        if (size(bounded) <= SUMMARY_BYTE_BUDGET)
            return bounded;
    }
    let destructive = [...plan.destructive];
    let more = plan.moreDestructive;
    while (destructive.length > 0 && size(bounded) > SUMMARY_BYTE_BUDGET) {
        const cut = Math.max(1, Math.ceil(destructive.length / 4));
        destructive = destructive.slice(0, destructive.length - cut);
        more += cut;
        plan = { ...plan, destructive, moreDestructive: more };
        bounded = { ...summary, plan };
    }
    return bounded;
};
/** Bytes as DynamoDB counts them, without `TextEncoder`, which `shared` cannot assume. */
export const utf8Length = (text) => {
    let bytes = 0;
    for (let i = 0; i < text.length; i += 1) {
        const c = text.charCodeAt(i);
        if (c < 0x80)
            bytes += 1;
        else if (c < 0x800)
            bytes += 2;
        else if (c >= 0xd800 && c <= 0xdbff) {
            // A surrogate pair is one four-byte character.
            bytes += 4;
            i += 1;
        }
        else
            bytes += 3;
    }
    return bytes;
};
export const headlineOf = (summary) => ({
    parsed: summary.parsed,
    shape: summary.shape,
    ...(summary.plan === undefined ? {} : { plan: summary.plan.counts }),
    ...(summary.plan === undefined || summary.plan.sensitiveCount === 0
        ? {}
        : { sensitive: summary.plan.sensitiveCount }),
    ...(summary.outcome === undefined ? {} : { outcome: summary.outcome }),
    ...(summary.timeline.totalSeconds === undefined
        ? {}
        : { seconds: summary.timeline.totalSeconds }),
});
/** `9m43s`, `1h02m`, `45s` — how long, the way the installer's reader reads it. */
export const formatSeconds = (seconds) => {
    const s = Math.round(seconds);
    if (s < 60)
        return `${String(s)}s`;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const rest = s % 60;
    if (h > 0)
        return `${String(h)}h${String(m).padStart(2, '0')}m`;
    return rest === 0 ? `${String(m)}m` : `${String(m)}m${String(rest).padStart(2, '0')}s`;
};
//# sourceMappingURL=log-parse.js.map